import { useState, useRef, useEffect } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Html5QrcodeScanner } from 'html5-qrcode'
import { ArrowLeft } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props extends Omit<AppShellProps, 'children'> {
  zone: { id: number; name: string | null }
  urls: AppShellProps['urls'] & {
    create: string
    checkOuts: string
  }
  translations: AppShellProps['translations'] & {
    checkOut: string
    scanQrCode: string
    scanning: string
    checkedOutSuccess: string
    notFound: string
    close: string
    scanAnother: string
    back: string
  }
}

interface ScanResult {
  status: 'success' | 'not_found' | 'error'
  name?: string
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function New(props: Props) {
  const { zone, urls, translations: t, ...shell } = props
  const scannerRef = useRef<Html5QrcodeScanner | null>(null)
  const processingRef = useRef(false)

  const [result, setResult] = useState<ScanResult | null>(null)

  /* ----- mount the scanner once, auto-starts with camera ----- */
  useEffect(() => {
    const scanner = new Html5QrcodeScanner(
      'qr-reader-checkout',
      { fps: 10, qrbox: { width: 250, height: 250 }, rememberLastUsedCamera: true },
      false,
    )
    scannerRef.current = scanner

    scanner.render(
      (decodedText) => {
        if (processingRef.current) return
        processingRef.current = true
        scanner.pause(true)
        handleScan(decodedText, scanner)
      },
      () => {},
    )

    return () => {
      scanner.clear().catch(() => {})
      scannerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ----- Check-out API ----- */
  async function handleScan(code: string, scanner: Html5QrcodeScanner) {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]')
    const csrfToken = csrfMeta?.getAttribute('content') || ''
    const currentTime = new Date()
      .toLocaleString('en-US', { timeZone: 'Asia/Riyadh' })
      .replace(', ', 'T')

    try {
      const res = await fetch(urls.create, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          check_out: { check_out_time: currentTime },
          user_id: code.trim(),
          zone_id: zone.id,
        }),
      })
      const data: ScanResult = await res.json()
      setResult(data)
    } catch {
      setResult({ status: 'not_found' })
    } finally {
      processingRef.current = false
    }
  }

  function dismissResult() {
    setResult(null)
    scannerRef.current?.resume()
  }

  const isSuccess = result?.status === 'success'

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={`${t.checkOut} — ${zone.name || 'Zone'}`} />

      <div className="mx-auto max-w-lg py-8 px-4">
        <Button
          variant="ghost"
          size="sm"
          className="mb-6 text-[#91918E]"
          onClick={() => router.visit(urls.checkOuts)}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t.back}
        </Button>

        <h1 className="text-xl font-semibold text-[#37352F] mb-2 text-center">{t.checkOut}</h1>
        <p className="text-sm text-[#91918E] text-center mb-6">{zone.name || 'Zone'}</p>

        {/* QR Scanner — always mounted, auto-starts camera */}
        <div id="qr-reader-checkout" className="mb-6" />

        {/* Result modal overlay */}
        {result && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="mx-4 w-full max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="rounded-xl overflow-hidden shadow-xl bg-white">
                {/* Header */}
                <div
                  className={`flex items-center justify-center py-8 ${
                    isSuccess ? 'bg-green-500' : 'bg-red-500'
                  }`}
                >
                  {isSuccess ? (
                    <svg className="h-16 w-16 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="h-16 w-16 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>

                {/* Body */}
                <div className="p-6 text-center">
                  {isSuccess ? (
                    <>
                      <h4 className="text-2xl font-semibold text-[#37352F] mb-2">Great</h4>
                      <p className="text-[#37352F]">
                        {result.name} {t.checkedOutSuccess}
                      </p>
                    </>
                  ) : (
                    <h4 className="text-2xl font-semibold text-[#37352F]">{t.notFound}</h4>
                  )}

                  <Button
                    className="mt-6 rounded-full bg-amber-500 hover:bg-amber-600 px-8"
                    onClick={dismissResult}
                  >
                    {t.close}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
